import React, { useState, useEffect, useRef } from "react";

import {
    Box,
    Paper,
    TextField,
    Grid,
    styled,
    Card,
    Divider,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Typography,
    InputAdornment,
    Switch,
    FormControlLabel,
    Link
} from '@mui/material';

import { grey } from "@mui/material/colors";

import dayjs from "dayjs";

import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from 'formik';
import { useSystemPrompt } from "../../../../../../hooks/useSystemPrompt"

import { useTranslations } from "../../../../../../hooks/useTranslations";
import { useLocations, locationTypes } from "../../../../../../hooks/api/useLocations";

import { checkValueExists } from "../../../../../../utils/dataSchema"

import {
    useContainer,
    containerInjectionProps,
    containerTypes,
    containerSizes,
    validationSchema
} from "../../../../hooks/model/useContainer"

import { useAppContextFeatures } from "../../../../../../context/AppContext";

import DatePickerComponent from "../../../../../../components/common/DatePickerComponent"
import CommonSectionHeader from "../../../../../../components/common/CommonSectionHeader";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export const StyledIcon = styled(Box)({
    width: 20,
    height: 20,
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
});

export default function UpdateContainerInformation({
    routeType,
    handleOnSuccess = () => { }
}) {
    const { t } = useTranslations({ ns: ["_@repositories", "_prompt", "common", "_common"] })

    const { id } = useParams();

    const { promptMessage } = useSystemPrompt();
    const { setSystemLoading } = useAppContextFeatures();

    const { list: listLocations } = useLocations({ locationType: locationTypes.location })

    const { getContainer, updateContainer } = useContainer({ routeType })

    const [savedValues, setSavedValues] = useState()
    const [dataToLocations, setDataLocations] = useState([])

    const loadData = async (id) => {

        setSystemLoading(true)

        try {

            const data = await getContainer({ id })

            const {
                repository,
                items,
                packing_file: packingFile
            } = data;

            setSavedValues({
                // ...initialValues,
                locationId: repository?.location_id,
                locationName: repository?.location_name,
                toLocationId: repository?.to_location_id,
                toLocationName: repository?.to_location_name,
                repoNumber: repository?.repo_number,
                repoAlternativeNumber: repository?.number_alternative,
                numberSeal: repository?.number_seal,
                repoMarking: repository?.number_marking,
                initialDate: dayjs(repository?.repo_date),
                loadDate: dayjs(repository?.load_date),
                supervisor: repository?.supervisor,
                shipper: repository?.shipper,
                customAgent: repository?.custom_agent,
                remark: repository?.remark ? repository.remark : "",
                containerSize: repository[containerInjectionProps.containerSize.retrieveKey],
                containerType: repository[containerInjectionProps.containerType.retrieveKey],
                sourceContainerRemId: repository[containerInjectionProps.sourceContainerRemId.retrieveKey],
                // ...initialRowValues,
                // packingFile,
                // rows,
            })

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

    }

    const onSubmit = async (currentValues) => {

        // console.log('currentValues?.sourceContainerRemId', currentValues?.sourceContainerRemId)

        if (!currentValues?.sourceContainerRemId) {
            promptMessage(false, `Source Container RemId is empty`)
            return false
        }

        setSystemLoading(true)

        try {

            let payload = {
                containerId: id,
                containerSize: currentValues?.containerSize,
                containerType: currentValues?.containerType,
                repository: {
                    repoNumber: currentValues?.repoNumber,
                    numberAlternative: currentValues?.repoAlternativeNumber,
                    repoDate: dayjs(currentValues?.initialDate).format("YYYY-MM-DD"),
                    remark: currentValues?.remark,
                },
                props: {
                    locationId: currentValues?.locationId,
                    toLocationId: currentValues?.toLocationId,
                    numberSeal: currentValues?.numberSeal,
                    numberMarking: currentValues?.repoMarking,
                    loadDate: dayjs(currentValues?.loadDate).format("YYYY-MM-DD"),
                    etaDate: '',
                    supervisor: currentValues?.supervisor,
                    shipper: currentValues?.shipper,
                    customAgent: currentValues?.customAgent
                },
            }

            let response = await updateContainer(payload)

            promptMessage(true, `${response?.message}`)

            if (handleOnSuccess) handleOnSuccess()

            return true

        } catch (error) {
            promptMessage(false, `Action Failed. (${error?.message})`)
        }

        setSystemLoading(false)

        return true
    }

    const formik = useFormik({
        initialValues: savedValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue, handleSubmit } = formik;

    useEffect(() => {

        loadData(id);

    }, [])

    useEffect(() => {

        if (values?.locationId) {
            fetchLocations({ excludeLocationId: values.locationId })
        }

    }, [values?.locationId])

    const fetchLocations = async ({ excludeLocationId = '' }) => {

        try {

            const props = {
                limit: -1,
                skip: 0,
                filterValue: [
                    // {
                    //     name: 'id',
                    //     operator: filterCustomer.anyOf,
                    //     value: JSON.stringify(productIds)
                    // }
                ],
                sortInfo: {
                    id: "id",
                    dir: 0
                },
            }

            props.locationType = locationTypes.location

            const result = await listLocations(props)

            let _locations = result?.data?.locations ? result.data.locations : []

            let locations = _locations.filter(i => i.id !== excludeLocationId)

            setDataLocations(locations)

        } catch (error) {
            promptMessage(false, `Action Failed (${error?.message})`)
        }

        return false

    }

    const RenderContainer = () => {

        if (!values) return <></>

        return (
            <Box sx={{ p: 2 }}>

                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={3} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t('container')}
                            subTitle={t('registeredContainerInformation')}
                        />

                        <Divider sx={{ my: 1 }} />

                        <Typography
                            sx={{
                                fontSize: 11,
                                color: grey[600]
                            }}
                        >
                            {`sourceContainerRemId: ${values?.sourceContainerRemId}`}
                        </Typography>

                    </Grid>
                    <Grid item xs={12} sm={12} md={9}>
                        <Grid container spacing={1}>

                            <Grid item xs={12} sm={12} md={6}>
                                {/* {JSON.stringify(values)} */}
                                <FormControl
                                    margin="dense"
                                    fullWidth
                                    size="small"
                                    error={Boolean(errors?.containerSize)}
                                >
                                    <InputLabel id="containerSize">{t('containerSize')}</InputLabel>
                                    <Select
                                        label={t('containerSize')}
                                        size="small"
                                        name={`containerSize`}
                                        sx={{
                                            width: '100%',
                                            // mr: 2
                                        }}
                                        onChange={(e) => {
                                            setFieldValue(`containerSize`, e.target.value)
                                        }}
                                        value={values?.containerSize}
                                    >
                                        {
                                            Object.keys(containerSizes).map((i, index) => {
                                                return (
                                                    <MenuItem key={index} value={containerSizes[i].value}>
                                                        {containerSizes[i].display}
                                                    </MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                    <FormHelperText>
                                        {errors?.containerSize}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={12} md={6}>
                                <FormControl
                                    margin="dense"
                                    fullWidth
                                    size="small"
                                    error={Boolean(errors?.containerType)}
                                >
                                    <InputLabel id="containerType">{t('containerType')}</InputLabel>
                                    <Select
                                        label={t('containerType')}
                                        size="small"
                                        name={`containerType`}
                                        sx={{
                                            width: '100%',
                                            // mr: 2
                                        }}
                                        onChange={(e) => {
                                            setFieldValue(`containerType`, e.target.value)
                                        }}
                                        value={values?.containerType}
                                    >
                                        {
                                            Object.keys(containerTypes).map((i, index) => {
                                                return (
                                                    <MenuItem key={index} value={containerTypes[i].value}>
                                                        {/* {containerTypes[i].display} */}
                                                        {t(containerTypes[i].id)}
                                                    </MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                    <FormHelperText>
                                        {errors?.containerType}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    autoFocus
                                    fullWidth
                                    name={"repoNumber"}
                                    label={t('repositoryNo')}
                                    type="text"
                                    variant="outlined"
                                    value={values?.repoNumber}
                                    onChange={handleChange}
                                    error={touched.repoNumber && Boolean(errors.repoNumber)}
                                    helperText={touched.repoNumber && errors.repoNumber}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"repoAlternativeNumber"}
                                    label={t('repositoryAltNo')}
                                    type="test"
                                    variant="outlined"
                                    value={values?.repoAlternativeNumber}
                                    onChange={handleChange}
                                    error={touched.repoAlternativeNumber && Boolean(errors.repoAlternativeNumber)}
                                    helperText={touched.repoAlternativeNumber && errors.repoAlternativeNumber}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"numberSeal"}
                                    label={t('sealNo')}
                                    type="text"
                                    variant="outlined"
                                    value={values?.numberSeal}
                                    onChange={handleChange}
                                    error={touched.numberSeal && Boolean(errors.numberSeal)}
                                    helperText={touched.numberSeal && errors.numberSeal}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"repoMarking"}
                                    label={t('repositoryMarking')}
                                    type="text"
                                    variant="outlined"
                                    value={values?.repoMarking}
                                    onChange={handleChange}
                                    error={touched.repoMarking && Boolean(errors.repoMarking)}
                                    helperText={touched.repoMarking && errors.repoMarking}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={6} textAlign={"left"}>

                                <DatePickerComponent
                                    config={{
                                        name: "initialDate",
                                        label: t('initialDate')
                                    }}
                                    formik={{
                                        values,
                                        errors,
                                        touched,
                                        handleChange,
                                        setFieldValue
                                    }}
                                    sx={{
                                        width: '100%'
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} textAlign={"left"}>

                                <DatePickerComponent
                                    config={{
                                        name: "loadDate",
                                        label: t('loadDate')
                                    }}
                                    formik={{
                                        values,
                                        errors,
                                        touched,
                                        handleChange,
                                        setFieldValue
                                    }}
                                    sx={{
                                        width: '100%'
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"supervisor"}
                                    label={t('supervisor')}
                                    type="text"
                                    variant="outlined"
                                    value={values?.supervisor}
                                    onChange={handleChange}
                                    error={touched.supervisor && Boolean(errors.supervisor)}
                                    helperText={touched.supervisor && errors.supervisor}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"shipper"}
                                    label={t('shipper')}
                                    type="text"
                                    variant="outlined"
                                    value={values?.shipper}
                                    onChange={handleChange}
                                    error={touched.shipper && Boolean(errors.shipper)}
                                    helperText={touched.shipper && errors.shipper}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"customAgent"}
                                    label={t('customAgent')}
                                    type="text"
                                    variant="outlined"
                                    value={values?.customAgent}
                                    onChange={handleChange}
                                    error={touched.customAgent && Boolean(errors.customAgent)}
                                    helperText={touched.customAgent && errors.customAgent}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"remark"}
                                    label={t('remark')}
                                    type="text"
                                    variant="outlined"
                                    multiline
                                    rows={"3"}
                                    value={values?.remark}
                                    onChange={handleChange}
                                    error={touched.remark && Boolean(errors.remark)}
                                    helperText={touched.remark && errors.remark}
                                />
                            </Grid>

                        </Grid>
                    </Grid>
                </Grid>
            </Box>

        )
    }

    const RenderFormLocation = () => {

        if (!values) return <></>

        return (
            <Box sx={{ p: 2 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                        <CommonSectionHeader
                            title={t('location')}
                            subTitle={t('relatedWarehouseLocationToProcess')}
                        />
                    </Grid>

                    <Grid item xs={12} sm={12} md={4.5} textAlign={"left"}>
                        <TextField
                            disabled
                            margin="dense"
                            size="small"
                            fullWidth
                            name={"locationId"}
                            label={t('location')}
                            type="text"
                            variant="outlined"
                            value={values.locationName}
                            error={touched.locationId && Boolean(errors.locationId)}
                            helperText={touched.locationId && errors.locationId}
                        />
                    </Grid>

                    <Grid item xs={12} sm={12} md={4.5}>
                        {/* {JSON.stringify(dataToLocations)} */}
                        <FormControl
                            margin="dense"
                            fullWidth
                            size="small"
                            error={Boolean(errors?.toLocationId)}
                        >
                            <InputLabel id="toLocationId">{t('destinationLocation')}</InputLabel>
                            <Select
                                label={t('destinationLocation')}
                                size="small"
                                name={`toLocationId`}
                                sx={{
                                    width: '100%',
                                    // mr: 2
                                }}
                                onChange={(event) => {

                                    let locId = event.target.value;

                                    // let locationSelected = availableLocations.filter(i => i.id === locId)

                                    setFieldValue(
                                        `toLocationId`,
                                        locId
                                    )

                                }}
                                value={
                                    checkValueExists({
                                        dataSearch: dataToLocations,
                                        valueToCompare: values?.toLocationId
                                    })
                                }
                            >
                                {
                                    dataToLocations?.map((i, index) => {
                                        return (
                                            <MenuItem key={index} value={i.id}>
                                                {i.location_name}
                                            </MenuItem>
                                        )
                                    })
                                }
                            </Select>
                            <FormHelperText>
                                {errors?.toLocationId}
                            </FormHelperText>
                        </FormControl>
                    </Grid>

                </Grid>
            </Box>
        )
    }

    const RenderForm = () => {

        if (!values) return <></>

        return (
            <form onSubmit={handleSubmit}>

                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                        {
                            RenderFormLocation()
                        }

                    </Grid>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12} sm={12} md={12}>
                        {
                            RenderContainer()
                        }
                    </Grid>
                </Grid>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <Button
                            type="submit"
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                        >
                            {t('saveChanges')}
                        </Button>
                    </Grid>
                </Grid>

            </form>
        )
    }

    return (
        <Box>


            {
                RenderForm()
            }

            {/* {
                isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            } */}

        </Box>
    )
}